import React, { useState, useEffect } from 'react';
import './AmazonQChat.scss';

interface AmazonQChatProps {
    stage: string;
    realm: string;
}

const AmazonQChat: React.FC<AmazonQChatProps> = ({ stage, realm }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Q Business is only available for Beta NAAmazon environment
    const isQBusinessEnvironment = stage === 'beta' && realm === 'NAAmazon';
    
    // Q Business URL provided by teammate
    const getQBusinessUrl = (): string => {
        // Only Beta NAAmazon is supported
        return 'https://pltljyu9.chat.qbusiness.us-east-1.on.aws/';
    };

    const qBusinessUrl = isQBusinessEnvironment ? getQBusinessUrl() : '';
    const isConfigured = qBusinessUrl && !qBusinessUrl.startsWith('REPLACE_WITH_');

    const toggleChat = () => {
        if (isConfigured) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                setIsLoading(true);
            }
        }
    };

    const closeChat = () => {
        setIsOpen(false);
        setIsLoading(false);
    };

    // Handle ESC key to close 
    useEffect(() => {
        if (!isQBusinessEnvironment || !isOpen) {
            return;
        }
        
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeChat();
            }
        };
        
        document.addEventListener('keydown', handleEsc);
        
        return () => {
            document.removeEventListener('keydown', handleEsc);
        };
    }, [isQBusinessEnvironment, isOpen]);

    // Handle iframe load
    const handleIframeLoad = () => {
        setIsLoading(false);
    };

    // Early return AFTER all hooks are declared
    if (!isQBusinessEnvironment) {
        return <></>;
    }

    return (
        <>
            {/* Floating Chat Button */}
            <div className={`floating-chat-button ${!isConfigured ? 'disabled' : ''}`}>
                <button
                    onClick={toggleChat}
                    className="chat-toggle-btn"
                    title={isConfigured ? 'Open Amazon Q Assistant' : 'Amazon Q not configured'}
                    disabled={!isConfigured}
                >
                    <div className="chat-icon">
                        {isConfigured ? (
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                {/* Amazon Q Business Hexagonal Logo */}
                                <path d="M12 1L20.5 6V18L12 23L3.5 18V6L12 1Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                <path d="M12 1L20.5 6V18L12 23L3.5 18V6L12 1Z" fill="currentColor" fillOpacity="0.1"/>
                                {/* Q shape inside hexagon */}
                                <circle cx="12" cy="11" r="4.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                <path d="M15 14L17 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                        )}
                    </div>
                    {!isConfigured && (
                        <div className="config-badge">Setup Required</div>
                    )}
                </button>

                {/* Environment indicator */}
                <div className="environment-indicator">
                    <span className="ask-amazon-q-badge">Ask Amazon Q</span>
                </div>
            </div>

            {/* Chat Modal */}
            {isOpen && (
                <div className="floating-chat-modal">
                    {/* Backdrop */}
                    <div className="chat-backdrop" onClick={closeChat}></div>
                    
                    {/* Modal Content */}
                    <div className="chat-modal-content">
                        <div className="chat-header">
                            <div className="header-left">
                                <h3>Amazon Q Assistant</h3>
                                <span className="env-badge">{stage} - {realm}</span>
                            </div>
                            <button 
                                className="close-btn" 
                                onClick={closeChat}
                                title="Close chat"
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                </svg>
                            </button>
                        </div>
                        
                        <div className="chat-body">
                            {isLoading && (
                                <div className="loading-overlay">
                                    <div className="loading-spinner"></div>
                                    <p>Loading Amazon Q Assistant...</p>
                                </div>
                            )}
                            
                            <iframe
                                src={qBusinessUrl}
                                width="100%"
                                height="100%"
                                frameBorder="0"
                                title="Amazon Q Business Assistant"
                                allow="microphone; camera; encrypted-media"
                                onLoad={handleIframeLoad}
                                style={{ display: isLoading ? 'none' : 'block' }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AmazonQChat;
